"use client";
import type { CookieBot } from "@/app/_utilities/cookiebot";

import { isOptedOutCCPA } from "@/app/_utilities/ccpa";
import { CookieName } from "@/app/_utilities/cookieKeys";
import { fetchIsUserInEU } from "@/app/_utilities/cookiebot";
import { IS_LOCAL, env } from "@/env";
import { generateId } from "@utilities/generateId";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import { logger } from "@utilities/logger";
import Cookies from "js-cookie";
import posthog, { type PostHog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import type { BootstrapData } from "../_analytics/posthog-types";
import { useEffect } from "react";

function getPostHogDistinctId() {
  let distinctID = "";
  const phCookieName = `ph_${env.NEXT_PUBLIC_POSTHOG_PUBLIC_KEY}_posthog`;
  const phCookie = Cookies.get(phCookieName);

  let newDistinctId = false;

  if (phCookie) {
    const phCookieParsed = JSON.parse(phCookie);
    distinctID = phCookieParsed.distinct_id;
  }
  if (!distinctID) {
    const anonCookie = Cookies.get(CookieName.AnonDistinctId);
    if (anonCookie) {
      distinctID = anonCookie;
    } else {
      distinctID = generateId();
      newDistinctId = true;
    }
  }

  return { distinctID, newDistinctId };
}

const initPostHog = (mode: string, bootstrapData: BootstrapData) => {
  if (!bootstrapData || posthog.__loaded) {
    return posthog;
  }

  // Adding cryptic name and random number to posthog endpoints in the hope adblockers will have a harder time detecting this endpoint
  const api_host = `${window.location.origin}/dachshund/${Math.floor(Math.random() * 1e6)}`;

  posthog.init(env.NEXT_PUBLIC_POSTHOG_PUBLIC_KEY, {
    api_host,
    flags_api_host: `${window.location.origin}/feature-flags`,
    persistence: mode === "memory" ? "memory" : "localStorage+cookie",
    loaded: posthog => {
      // Enable debug mode in development
      if (IS_LOCAL) {
        posthog.debug();
        logger.info("[PostHog] Loaded feature flags", bootstrapData.featureFlags);
      }
    },
    capture_pageview: false, // Automatic capture is unreliable with nextjs, so we use our own hook
    autocapture: false, // Disable automatic capture as too expensive for our volume, instead we use manual events
    bootstrap: {
      distinctID: bootstrapData.distinctID,
      featureFlags: bootstrapData.featureFlags || {},
      featureFlagPayloads: bootstrapData.featureFlagPayloads || {},
    },
    session_recording: {
      maskAllInputs: false, // this enables recording of the input values for understanding
      // how the users are using projects / speech synthesis
      maskInputOptions: {
        password: true,
      },
    },
    disable_session_recording: true,
    disable_surveys: true,
    capture_exceptions: true,
  });

  if (isOptedOutCCPA()) {
    posthogOptOut();
  }

  return posthog;
};

const defaultInit = (bootstrapData: BootstrapData, isUserInEU: boolean) => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const consentDecision = getPhConsentDecision();
  let ph: PostHog;
  if (isUserInEU === false) {
    ph = initPostHog("localStorage+cookie", bootstrapData);
    ph.register({ consent_state: "not_applicable" });
    ph.register({ persistence: "localStorage+cookie" });
  } else if (isUserInEU && consentDecision === "true") {
    ph = initPostHog("localStorage+cookie", bootstrapData);
    ph.register({ consent_state: "granted" });
    ph.register({ persistence: "localStorage+cookie" });
  } else {
    ph = initPostHog("memory", bootstrapData);
    ph.register({ consent_state: "undecided" });
    ph.register({ persistence: "memory" });
  }

  ph.register({ mw_is_first_visit: bootstrapData.isFirstVisit ? "true" : "false" });
  return ph;
};

const getPhConsentDecision = (): null | string => {
  try {
    return safeLocalStorage.getItem(LocalStorageKeys.PH_CONSENT_DECISION);
  } catch (e) {
    return null;
  }
};

const createPostHogClient = ({ bootstrapData }: { bootstrapData: BootstrapData }): void => {
  const isUserInEU = fetchIsUserInEU();
  defaultInit(bootstrapData, isUserInEU);

  const updateConsentInfo = () => {
    const updatedConsentInfo = { ...window.Cookiebot };
    const consentDecision = updatedConsentInfo.consent.statistics;
    setConsentDecision(consentDecision);
    updatePostHogConsent(updatedConsentInfo);
  };

  // Early init if cookiebot is already loaded
  if (window.Cookiebot?.consent) {
    updateConsentInfo();
  }

  // Listen for when the Cookiebot dialog loads
  window.addEventListener("CookiebotOnDialogInit", updateConsentInfo);

  // Listen for consent updates
  window.addEventListener("CookiebotOnConsentReady", updateConsentInfo);

  function setConsentDecision(consentDecision: boolean) {
    try {
      safeLocalStorage.setItem(LocalStorageKeys.PH_CONSENT_DECISION, consentDecision.toString());
      return true;
    } catch (e) {
      return false;
    }
  }

  function updatePostHogConsent(consentInfo: CookieBot) {
    // User is not in EU and isUserInEU hadn't loaded yet in defaultInit
    if (!isUserInEU) {
      posthog.set_config({ persistence: "localStorage+cookie" });
      posthog.register({ consent_state: "not_applicable" });
      posthog.register({ persistence: "localStorage+cookie" });
    }

    // User has denied consent, keep as memory but record consent decision
    if (consentInfo?.hasResponse === true && consentInfo?.consent?.statistics === false) {
      posthog?.register({ consent_state: "denied" });
    }

    // User has granted consent, update persistence
    if (consentInfo?.hasResponse === true && consentInfo?.consent?.statistics === true) {
      posthog.set_config({ persistence: "localStorage+cookie" });
      posthog?.register({ consent_state: "granted" });
      posthog?.register({ persistence: "localStorage+cookie" });
    }
  }
};

export function PHProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const bootstrapData = Cookies.get(CookieName.BootstrapData);
    const bootstrapDataObject = JSON.parse(bootstrapData || "{}") as BootstrapData;
    const distinctID = bootstrapDataObject.distinctID || getPostHogDistinctId().distinctID;

    // Pass full bootstrap data including feature flags and payloads
    createPostHogClient({
      bootstrapData: {
        distinctID,
        isFirstVisit: bootstrapDataObject.isFirstVisit ?? false,
        featureFlags: bootstrapDataObject.featureFlags || {},
        featureFlagPayloads: bootstrapDataObject.featureFlagPayloads || {},
      },
    });
  }, []);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

export const posthogOptOut = () => {
  posthog.opt_out_capturing();
  posthog.stopSessionRecording();
};
